import { apiService } from './api';
import { 
  LoginRequest, 
  LoginResponse, 
  RegisterRequest,
  RegisterResponse,
  ActivateAccountRequest,
  ActivateAccountResponse,
  ResendActivationRequest,
  ResendActivationResponse,
  ForgotPasswordRequest, 
  ResetPasswordRequest,
  ChangePasswordRequest,
  UserProfile
} from '../types/auth.types';

export const authService = {
  // Authentication
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiService.post<LoginResponse>('/auth/login/', credentials);
    if (response.tokens?.access) {
      localStorage.setItem('access_token', response.tokens.access);
      localStorage.setItem('refresh_token', response.tokens.refresh);
    }
    return response;
  },

  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    return await apiService.post<RegisterResponse>('/auth/register/', data);
  },

  activateAccount: async (data: ActivateAccountRequest): Promise<ActivateAccountResponse> => {
    const response = await apiService.post<ActivateAccountResponse>('/auth/activate/', data);
    if (response.tokens?.access) {
      localStorage.setItem('access_token', response.tokens.access);
      localStorage.setItem('refresh_token', response.tokens.refresh);
    }
    return response;
  },

  resendActivationCode: async (data: ResendActivationRequest): Promise<ResendActivationResponse> => {
    return await apiService.post<ResendActivationResponse>('/auth/activate/resend/', data);
  },

  logout: async (): Promise<void> => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await apiService.post('/auth/logout/', { refresh_token: refreshToken });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  refreshToken: async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return null;

    try {
      const response = await apiService.post<{ access: string }>('/auth/token/refresh/', {
        refresh: refreshToken,
      });
      localStorage.setItem('access_token', response.access);
      return response.access;
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return null;
    }
  },

  // Password management
  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    await apiService.post('/auth/password/reset/', data);
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    await apiService.post('/auth/password/reset/confirm/', data);
  },

  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    await apiService.post('/auth/password/change/', data);
  },

  // User profile
  getProfile: async (): Promise<UserProfile> => {
    return await apiService.get<UserProfile>('/auth/me/');
  },

  updateProfile: async (data: Partial<UserProfile>): Promise<UserProfile> => {
    return await apiService.patch<UserProfile>('/auth/me/', data);
  },

  // 2FA
  setupTwoFactor: async (): Promise<{ secret: string; otpauth_url: string; issuer: string }> => {
    return await apiService.post('/auth/2fa/setup/', {});
  },

  verifyTwoFactor: async (code: string): Promise<{ message: string }> => {
    return await apiService.post('/auth/2fa/verify/', { code });
  },

  disableTwoFactor: async (code: string): Promise<{ message: string }> => {
    return await apiService.post('/auth/2fa/disable/', { code });
  },

  // Check authentication status
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('access_token');
    return !!token;
  },

  // Get current user info
  getCurrentUser: async (): Promise<UserProfile | null> => {
    if (!authService.isAuthenticated()) return null;
    
    try {
      return await authService.getProfile();
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  },
};